/**
 * Neurula Reporting Redux Slice
 * 
 * Manages state for all reporting-related operations including:
 * - Reports CRUD operations
 * - Templates management
 * - Filters management
 * - AI report generation
 * - Export operations
 * - UI state (loading, errors, selections)
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import reportingService from '../../services/reportingService';

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
    // Reports
    reports: {
        list: [],
        currentReport: null,
        loading: false,
        error: null,
        pagination: {
            total: 0,
            page: 1,
            size: 10,
            totalPages: 0
        }
    },
    
    // Templates
    templates: {
        list: [],
        currentTemplate: null,
        loading: false,
        error: null,
        pagination: {
            total: 0,
            page: 1,
            size: 10,
            totalPages: 0
        }
    },
    
    // Filters
    filters: {
        list: [],
        currentFilter: null,
        appliedFilters: [],
        loading: false,
        error: null
    },
    
    // AI Reports
    aiReports: {
        currentQuery: '',
        suggestions: [],
        examples: [],
        interpretedPrompt: null,
        generatedReport: null,
        loading: false,
        error: null,
        confidence: null
    },
    
    // Exports
    exports: {
        activeExports: [],
        exportHistory: [],
        loading: false,
        error: null
    },
    
    // UI State
    ui: {
        selectedReportIds: [],
        viewMode: 'grid', // 'grid' | 'list'
        sortBy: 'created_at',
        sortOrder: 'desc',
        searchQuery: '',
        activeModal: null, // 'create' | 'edit' | 'preview' | 'export' | null
        sidebarCollapsed: false
    },
    
    // System
    system: {
        stats: null,
        reportTypes: [],
        loading: false,
        error: null
    }
};

// ============================================================================
// ASYNC THUNKS - REPORTS
// ============================================================================

export const fetchReports = createAsyncThunk(
    'reporting/fetchReports',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await reportingService.getReports(params);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const createReport = createAsyncThunk(
    'reporting/createReport',
    async (reportData, { rejectWithValue }) => {
        try {
            const response = await reportingService.createReport(reportData);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const generateReportFromTemplate = createAsyncThunk(
    'reporting/generateReportFromTemplate',
    async (reportGenerationRequest, { rejectWithValue }) => {
        try {
            const response = await reportingService.generateReportFromTemplate(reportGenerationRequest);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchReportById = createAsyncThunk(
    'reporting/fetchReportById',
    async (reportId, { rejectWithValue }) => {
        try {
            const response = await reportingService.getReportById(reportId);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updateReport = createAsyncThunk(
    'reporting/updateReport',
    async ({ reportId, updateData }, { rejectWithValue }) => {
        try {
            const response = await reportingService.updateReport(reportId, updateData);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return { reportId, ...response };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const deleteReport = createAsyncThunk(
    'reporting/deleteReport',
    async (reportId, { rejectWithValue }) => {
        try {
            const response = await reportingService.deleteReport(reportId);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return { reportId };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const generateReport = createAsyncThunk(
    'reporting/generateReport',
    async ({ reportId, params }, { rejectWithValue }) => {
        try {
            const response = await reportingService.generateReport(reportId, params);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return { reportId, ...response };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// ============================================================================
// ASYNC THUNKS - TEMPLATES
// ============================================================================

export const fetchTemplates = createAsyncThunk(
    'reporting/fetchTemplates',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await reportingService.getTemplates(params);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const createTemplate = createAsyncThunk(
    'reporting/createTemplate',
    async (templateData, { rejectWithValue }) => {
        try {
            const response = await reportingService.createTemplate(templateData);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchTemplateById = createAsyncThunk(
    'reporting/fetchTemplateById',
    async (templateId, { rejectWithValue }) => {
        try {
            const response = await reportingService.getTemplateById(templateId);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updateTemplate = createAsyncThunk(
    'reporting/updateTemplate',
    async ({ templateId, updateData }, { rejectWithValue }) => {
        try {
            const response = await reportingService.updateTemplate(templateId, updateData);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return { templateId, ...response };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const deleteTemplate = createAsyncThunk(
    'reporting/deleteTemplate',
    async (templateId, { rejectWithValue }) => {
        try {
            const response = await reportingService.deleteTemplate(templateId);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return { templateId };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const cloneTemplate = createAsyncThunk(
    'reporting/cloneTemplate',
    async ({ templateId, cloneData }, { rejectWithValue }) => {
        try {
            const response = await reportingService.cloneTemplate(templateId, cloneData);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// ============================================================================
// ASYNC THUNKS - FILTERS
// ============================================================================

export const fetchFilters = createAsyncThunk(
    'reporting/fetchFilters',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await reportingService.getFilters(params);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const createFilter = createAsyncThunk(
    'reporting/createFilter',
    async (filterData, { rejectWithValue }) => {
        try {
            const response = await reportingService.createFilter(filterData);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const applyFilter = createAsyncThunk(
    'reporting/applyFilter',
    async ({ filterId, testData }, { rejectWithValue }) => {
        try {
            const response = await reportingService.applyFilter(filterId, testData);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return { filterId, ...response };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// ============================================================================
// ASYNC THUNKS - AI REPORTS
// ============================================================================

export const generateAIReport = createAsyncThunk(
    'reporting/generateAIReport',
    async (aiReportData, { rejectWithValue }) => {
        try {
            const response = await reportingService.generateAIReport(aiReportData);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchAISuggestions = createAsyncThunk(
    'reporting/fetchAISuggestions',
    async (context = {}, { rejectWithValue }) => {
        try {
            const response = await reportingService.getAISuggestions(context);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const interpretAIPrompt = createAsyncThunk(
    'reporting/interpretAIPrompt',
    async (promptData, { rejectWithValue }) => {
        try {
            const response = await reportingService.interpretAIQuery(promptData);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchAIExamples = createAsyncThunk(
    'reporting/fetchAIExamples',
    async (category = null, { rejectWithValue }) => {
        try {
            const response = await reportingService.getAIExamples(category);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// ============================================================================
// ASYNC THUNKS - EXPORTS
// ============================================================================

export const exportReport = createAsyncThunk(
    'reporting/exportReport',
    async ({ reportId, exportOptions }, { rejectWithValue }) => {
        try {
            const response = await reportingService.exportReport(reportId, exportOptions);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return { reportId, ...response };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const exportMultipleReports = createAsyncThunk(
    'reporting/exportMultipleReports',
    async ({ reportIds, exportOptions }, { rejectWithValue }) => {
        try {
            const response = await reportingService.exportMultipleReports(reportIds, exportOptions);
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// ============================================================================
// ASYNC THUNKS - SYSTEM
// ============================================================================

export const fetchSystemStats = createAsyncThunk(
    'reporting/fetchSystemStats',
    async (_, { rejectWithValue }) => {
        try {
            const response = await reportingService.getSystemStats();
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchReportTypes = createAsyncThunk(
    'reporting/fetchReportTypes',
    async (_, { rejectWithValue }) => {
        try {
            const response = await reportingService.getReportTypes();
            if (!response.success) {
                return rejectWithValue(response.message);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// ============================================================================
// SLICE DEFINITION
// ============================================================================

const reportingSlice = createSlice({
    name: 'reporting',
    initialState,
    reducers: {
        // UI Actions
        setViewMode: (state, action) => {
            state.ui.viewMode = action.payload;
        },
        
        setSortBy: (state, action) => {
            state.ui.sortBy = action.payload.field;
            state.ui.sortOrder = action.payload.order;
        },
        
        setSearchQuery: (state, action) => {
            state.ui.searchQuery = action.payload;
        },
        
        setActiveModal: (state, action) => {
            state.ui.activeModal = action.payload;
        },
        
        toggleSidebar: (state) => {
            state.ui.sidebarCollapsed = !state.ui.sidebarCollapsed;
        },
        
        selectReport: (state, action) => {
            const reportId = action.payload;
            if (state.ui.selectedReportIds.includes(reportId)) {
                state.ui.selectedReportIds = state.ui.selectedReportIds.filter(id => id !== reportId);
            } else {
                state.ui.selectedReportIds.push(reportId);
            }
        },
        
        selectAllReports: (state) => {
            state.ui.selectedReportIds = state.reports.list.map(report => report.id);
        },
        
        clearSelectedReports: (state) => {
            state.ui.selectedReportIds = [];
        },
        
        // Filter Actions
        addAppliedFilter: (state, action) => {
            state.filters.appliedFilters.push(action.payload);
        },
        
        removeAppliedFilter: (state, action) => {
            state.filters.appliedFilters = state.filters.appliedFilters.filter(
                filter => filter.id !== action.payload
            );
        },
        
        clearAppliedFilters: (state) => {
            state.filters.appliedFilters = [];
        },
        
        // AI Reports Actions
        setAIQuery: (state, action) => {
            state.aiReports.currentQuery = action.payload;
        },
        
        clearAIReport: (state) => {
            state.aiReports.generatedReport = null;
            state.aiReports.currentQuery = '';
        },
        
        // Export Actions
        addActiveExport: (state, action) => {
            state.exports.activeExports.push(action.payload);
        },
        
        removeActiveExport: (state, action) => {
            state.exports.activeExports = state.exports.activeExports.filter(
                exp => exp.id !== action.payload
            );
        },
        
        // Clear errors
        clearReportsError: (state) => {
            state.reports.error = null;
        },
        
        clearTemplatesError: (state) => {
            state.templates.error = null;
        },
        
        clearFiltersError: (state) => {
            state.filters.error = null;
        },
        
        clearAIReportsError: (state) => {
            state.aiReports.error = null;
        },
        
        clearExportsError: (state) => {
            state.exports.error = null;
        },
        
        clearSystemError: (state) => {
            state.system.error = null;
        }
    },
    
    extraReducers: (builder) => {
        // ====================================================================
        // REPORTS REDUCERS
        // ====================================================================
        
        // Fetch Reports
        builder
            .addCase(fetchReports.pending, (state) => {
                state.reports.loading = true;
                state.reports.error = null;
            })
            .addCase(fetchReports.fulfilled, (state, action) => {
                state.reports.loading = false;
                // Handle both direct array and wrapped data formats
                const data = action.payload.data;
                state.reports.list = Array.isArray(data) ? data : (data?.items || data?.reports || []);
                
                // Handle pagination metadata from various response formats
                const meta = action.payload.meta || data?.meta || action.payload;
                if (meta && (meta.total !== undefined || meta.total_count !== undefined)) {
                    state.reports.pagination = {
                        total: meta.total || meta.total_count || 0,
                        page: meta.page || meta.current_page || 1,
                        size: meta.size || meta.page_size || 10,
                        totalPages: meta.total_pages || Math.ceil((meta.total || meta.total_count || 0) / (meta.size || meta.page_size || 10))
                    };
                }
            })
            .addCase(fetchReports.rejected, (state, action) => {
                state.reports.loading = false;
                state.reports.error = action.payload;
            });
            
        // Create Report
        builder
            .addCase(createReport.pending, (state) => {
                state.reports.loading = true;
                state.reports.error = null;
            })
            .addCase(createReport.fulfilled, (state, action) => {
                state.reports.loading = false;
                state.reports.list.unshift(action.payload.data);
            })
            .addCase(createReport.rejected, (state, action) => {
                state.reports.loading = false;
                state.reports.error = action.payload;
            });
            
        // Generate Report From Template
        builder
            .addCase(generateReportFromTemplate.pending, (state) => {
                state.reports.loading = true;
                state.reports.error = null;
            })
            .addCase(generateReportFromTemplate.fulfilled, (state, action) => {
                state.reports.loading = false;
                // Add the new report instance to the list
                if (action.payload.data) {
                    state.reports.list.unshift(action.payload.data);
                }
            })
            .addCase(generateReportFromTemplate.rejected, (state, action) => {
                state.reports.loading = false;
                state.reports.error = action.payload;
            });
            
        // Fetch Report By ID
        builder
            .addCase(fetchReportById.pending, (state) => {
                state.reports.loading = true;
                state.reports.error = null;
            })
            .addCase(fetchReportById.fulfilled, (state, action) => {
                state.reports.loading = false;
                state.reports.currentReport = action.payload.data;
            })
            .addCase(fetchReportById.rejected, (state, action) => {
                state.reports.loading = false;
                state.reports.error = action.payload;
            });
            
        // Update Report
        builder
            .addCase(updateReport.fulfilled, (state, action) => {
                const index = state.reports.list.findIndex(report => report.id === action.payload.reportId);
                if (index !== -1) {
                    state.reports.list[index] = { ...state.reports.list[index], ...action.payload.data };
                }
                if (state.reports.currentReport?.id === action.payload.reportId) {
                    state.reports.currentReport = { ...state.reports.currentReport, ...action.payload.data };
                }
            });
            
        // Delete Report
        builder
            .addCase(deleteReport.fulfilled, (state, action) => {
                state.reports.list = state.reports.list.filter(report => report.id !== action.payload.reportId);
                if (state.reports.currentReport?.id === action.payload.reportId) {
                    state.reports.currentReport = null;
                }
            });
            
        // ====================================================================
        // TEMPLATES REDUCERS
        // ====================================================================
        
        // Fetch Templates
        builder
            .addCase(fetchTemplates.pending, (state) => {
                state.templates.loading = true;
                state.templates.error = null;
            })
            .addCase(fetchTemplates.fulfilled, (state, action) => {
                state.templates.loading = false;
                // Handle both direct array and wrapped data formats
                const data = action.payload.data;
                state.templates.list = Array.isArray(data) ? data : (data?.items || data?.templates || []);
                
                // Handle pagination metadata from various response formats
                const meta = action.payload.meta || data?.meta || action.payload;
                if (meta && (meta.total !== undefined || meta.total_count !== undefined)) {
                    state.templates.pagination = {
                        total: meta.total || meta.total_count || 0,
                        page: meta.page || meta.current_page || 1,
                        size: meta.size || meta.page_size || 10,
                        totalPages: meta.total_pages || Math.ceil((meta.total || meta.total_count || 0) / (meta.size || meta.page_size || 10))
                    };
                }
            })
            .addCase(fetchTemplates.rejected, (state, action) => {
                state.templates.loading = false;
                state.templates.error = action.payload;
            });
            
        // Create Template
        builder
            .addCase(createTemplate.fulfilled, (state, action) => {
                state.templates.list.unshift(action.payload.data);
            });
            
        // Clone Template
        builder
            .addCase(cloneTemplate.fulfilled, (state, action) => {
                state.templates.list.unshift(action.payload.data);
            });
            
        // ====================================================================
        // FILTERS REDUCERS
        // ====================================================================
        
        // Fetch Filters
        builder
            .addCase(fetchFilters.pending, (state) => {
                state.filters.loading = true;
                state.filters.error = null;
            })
            .addCase(fetchFilters.fulfilled, (state, action) => {
                state.filters.loading = false;
                state.filters.list = action.payload.data;
            })
            .addCase(fetchFilters.rejected, (state, action) => {
                state.filters.loading = false;
                state.filters.error = action.payload;
            });
            
        // Create Filter
        builder
            .addCase(createFilter.fulfilled, (state, action) => {
                state.filters.list.unshift(action.payload.data);
            });
            
        // ====================================================================
        // AI REPORTS REDUCERS
        // ====================================================================
        
        // Generate AI Report
        builder
            .addCase(generateAIReport.pending, (state) => {
                state.aiReports.loading = true;
                state.aiReports.error = null;
            })
            .addCase(generateAIReport.fulfilled, (state, action) => {
                state.aiReports.loading = false;
                state.aiReports.generatedReport = action.payload.data;
            })
            .addCase(generateAIReport.rejected, (state, action) => {
                state.aiReports.loading = false;
                state.aiReports.error = action.payload;
            });
            
        // Fetch AI Suggestions
        builder
            .addCase(fetchAISuggestions.pending, (state) => {
                state.aiReports.loading = true;
                state.aiReports.error = null;
            })
            .addCase(fetchAISuggestions.fulfilled, (state, action) => {
                state.aiReports.loading = false;
                state.aiReports.suggestions = action.payload.data.suggestions || action.payload.data;
            })
            .addCase(fetchAISuggestions.rejected, (state, action) => {
                state.aiReports.loading = false;
                state.aiReports.error = action.payload;
            });
            
        // Interpret AI Prompt
        builder
            .addCase(interpretAIPrompt.pending, (state) => {
                state.aiReports.loading = true;
                state.aiReports.error = null;
            })
            .addCase(interpretAIPrompt.fulfilled, (state, action) => {
                state.aiReports.loading = false;
                state.aiReports.interpretedPrompt = action.payload.data;
                state.aiReports.confidence = action.payload.data.confidence_score;
            })
            .addCase(interpretAIPrompt.rejected, (state, action) => {
                state.aiReports.loading = false;
                state.aiReports.error = action.payload;
            });
            
        // Fetch AI Examples
        builder
            .addCase(fetchAIExamples.pending, (state) => {
                state.aiReports.loading = true;
                state.aiReports.error = null;
            })
            .addCase(fetchAIExamples.fulfilled, (state, action) => {
                state.aiReports.loading = false;
                state.aiReports.examples = action.payload.data.examples || action.payload.data;
            })
            .addCase(fetchAIExamples.rejected, (state, action) => {
                state.aiReports.loading = false;
                state.aiReports.error = action.payload;
            });
            
        // ====================================================================
        // EXPORTS REDUCERS
        // ====================================================================
        
        // Export Report
        builder
            .addCase(exportReport.pending, (state) => {
                state.exports.loading = true;
                state.exports.error = null;
            })
            .addCase(exportReport.fulfilled, (state, action) => {
                state.exports.loading = false;
                // Add to export history
                state.exports.exportHistory.unshift({
                    id: Date.now(),
                    reportId: action.payload.reportId,
                    timestamp: new Date().toISOString(),
                    status: 'completed'
                });
            })
            .addCase(exportReport.rejected, (state, action) => {
                state.exports.loading = false;
                state.exports.error = action.payload;
            });
            
        // ====================================================================
        // SYSTEM REDUCERS
        // ====================================================================
        
        // Fetch System Stats
        builder
            .addCase(fetchSystemStats.pending, (state) => {
                state.system.loading = true;
                state.system.error = null;
            })
            .addCase(fetchSystemStats.fulfilled, (state, action) => {
                state.system.loading = false;
                // Handle both direct data and wrapped response formats
                state.system.stats = action.payload.data || action.payload;
            })
            .addCase(fetchSystemStats.rejected, (state, action) => {
                state.system.loading = false;
                state.system.error = action.payload;
            });
            
        // Fetch Report Types
        builder
            .addCase(fetchReportTypes.fulfilled, (state, action) => {
                state.system.reportTypes = action.payload.data;
            });
    }
});

// ============================================================================
// EXPORTS
// ============================================================================

export const {
    setViewMode,
    setSortBy,
    setSearchQuery,
    setActiveModal,
    toggleSidebar,
    selectReport,
    selectAllReports,
    clearSelectedReports,
    addAppliedFilter,
    removeAppliedFilter,
    clearAppliedFilters,
    setAIQuery,
    clearAIReport,
    addActiveExport,
    removeActiveExport,
    clearReportsError,
    clearTemplatesError,
    clearFiltersError,
    clearAIReportsError,
    clearExportsError,
    clearSystemError
} = reportingSlice.actions;

// Selectors
export const selectReports = (state) => state.reporting.reports.list;
export const selectCurrentReport = (state) => state.reporting.reports.currentReport;
export const selectReportsLoading = (state) => state.reporting.reports.loading;
export const selectReportsError = (state) => state.reporting.reports.error;
export const selectReportsPagination = (state) => state.reporting.reports.pagination;

export const selectTemplates = (state) => state.reporting.templates.list;
export const selectCurrentTemplate = (state) => state.reporting.templates.currentTemplate;
export const selectTemplatesLoading = (state) => state.reporting.templates.loading;

export const selectFilters = (state) => state.reporting.filters.list;
export const selectAppliedFilters = (state) => state.reporting.filters.appliedFilters;
export const selectFiltersLoading = (state) => state.reporting.filters.loading;

export const selectAIReports = (state) => state.reporting.aiReports;
export const selectAISuggestions = (state) => state.reporting.aiReports.suggestions;
export const selectAIExamples = (state) => state.reporting.aiReports.examples;
export const selectInterpretedPrompt = (state) => state.reporting.aiReports.interpretedPrompt;
export const selectAIConfidence = (state) => state.reporting.aiReports.confidence;
export const selectGeneratedAIReport = (state) => state.reporting.aiReports.generatedReport;
export const selectAIReportsLoading = (state) => state.reporting.aiReports.loading;
export const selectAIReportsError = (state) => state.reporting.aiReports.error;

export const selectExports = (state) => state.reporting.exports;
export const selectActiveExports = (state) => state.reporting.exports.activeExports;

export const selectUI = (state) => state.reporting.ui;
export const selectSelectedReportIds = (state) => state.reporting.ui.selectedReportIds;
export const selectViewMode = (state) => state.reporting.ui.viewMode;

export const selectSystemStats = (state) => state.reporting.system.stats;
export const selectReportTypes = (state) => state.reporting.system.reportTypes;

export default reportingSlice.reducer;